import { h, Component } from 'preact';
import { sortObjectByOrder, joinClasses } from 'utils/utils';
import { getHeaderHeight } from 'utils/bolt';
import { maybeNavInternally } from 'utils/bolt/markdown';
import HTML from 'components/core/html/html.js';
import s from 'components/bolt/quotesCarousel/quotesCarousel.sss';
import { getValueForClientSize } from 'utils/bolt/index';


export default class QuotesCarousel extends Component {
	constructor(props) {
		super(props);
		this.defaultCarouselHeight = 100;
		this.items = sortObjectByOrder(props.data?.items) || [];
		this.state = {
			activeIndex: 0,
			exitingIndex: null,
			headerHeight: 0,
			carouselHeight: this.defaultCarouselHeight
		};
	}

	componentDidMount() {
		if (!this.props.isShowtimes) {
			this.getTopGap();
			this.setCarouselHeight();
		}
		if (this.items.length > 1) {
			this.startCarousel();
		}
	}

	componentDidUpdate(prevProps) {
		const prevDeviceSize = prevProps.client?.deviceSize;
		const deviceSize = this.props.client?.deviceSize;
		if (deviceSize !== prevDeviceSize) {
			this.getTopGap();
		}
		const prevDeviceWidth = prevProps.client?.dimensions?.width;
		const deviceWidth = this.props.client?.dimensions?.width;
		if (prevDeviceWidth !== deviceWidth) {
			this.setCarouselHeight();
		}

	}

	componentWillUnmount() {
		this.timer && window.clearTimeout(this.timer);
	}

	getTopGap() {
		const headerHeight = getHeaderHeight();
		this.setState({
			headerHeight
		});
	}

	setCarouselHeight() {
		const itemContainers = [...document.querySelectorAll(`.${s.itemContainer}`)];
		if (!itemContainers || itemContainers.length < 1) return;

		const containerHeights = itemContainers.map((item) => {
			let totalHeight = 0;
			const itemChildren = [...item.children];
			itemChildren.forEach(child => {
				totalHeight += (child.clientHeight || 0);
			});
			return totalHeight;
		});
		const tallestItem = Math.max(...containerHeights);
		const desiredCarouselHeight = tallestItem > this.defaultCarouselHeight ? tallestItem : this.defaultCarouselHeight;
		if (this.state.carouselHeight <= this.defaultCarouselHeight && desiredCarouselHeight <= this.defaultCarouselHeight) {
			return;
		}
		this.setState({
			carouselHeight: desiredCarouselHeight
		});
	}

	startCarousel(isNotFirstCall) {
		const itemDuration = +(this.props.data?.itemDuration?.trim() || 3) * 1000;
		const transitionDuration = +(this.props.data?.transitionDuration?.trim() || 2) * 1000;
		const delay = isNotFirstCall ? itemDuration + transitionDuration : itemDuration;

		this.timer = window.setTimeout(() => {
			const currentActiveIndex = this.state.activeIndex;
			const nextActiveIndex = this.state.activeIndex === this.items.length - 1 ? 0 : currentActiveIndex + 1;
			this.setState({
				activeIndex: nextActiveIndex,
				exitingIndex: currentActiveIndex
			});
			this.startCarousel(true);
		}, delay);
	}

	renderItem(item, index) {
		const rows = (sortObjectByOrder(item.contentRows) || []).slice(0, 2);
		const authorData = item.author || {};
		const authorPositioning = getValueForClientSize(authorData.positioning);
		const authorStacked = !authorPositioning || authorPositioning === 'stacked';
		const authorFirstRow = authorPositioning === 'inlineRow1';
		const authorSecondRow = authorPositioning === 'inlineRow2';
		const { activeIndex, exitingIndex } = this.state;
		const isActive = activeIndex === index;
		const isExiting = exitingIndex === index;
		const transitionFade = this.props.data?.transitionStyle === 'fade';
		const transitionDuration = this.props.data?.transitionDuration?.trim() || '2';
		const itemContainerStyle = {
			transition: `transform ${transitionDuration}s, opacity ${transitionDuration}s`,
			animationDuration: `${transitionDuration}s`
		};
		const alignment = getValueForClientSize(this.props.data?.alignment) || 'center';
		const leftAligned = alignment === 'left';
		const rightAligned = alignment === 'right';

		const author = authorData.content?.startsWith('https') ? (
			<img
				class={joinClasses(s.authorImage, authorStacked && s.authorStacked)}
				src={authorData.content}
				alt={authorData.imageAlt || 'logo'}
			/>
		) : (
			<HTML
				class={joinClasses(s.author, authorStacked && s.authorStacked)}
				markdown
				content={authorData.content}
				onClickCapture={maybeNavInternally}
			/>
		);

		return (
			<div
				class={
					joinClasses(
						s.itemContainer,
						transitionFade && s.transitionFade,
						isActive && s.active,
						isExiting && s.exiting,
						leftAligned && s.leftAligned,
						rightAligned && s.rightAligned
					)}
				style={itemContainerStyle}
				key={`item${index}`}
			>
				{rows.length > 0 && rows.map((row, i) => {
					const authorInline = (i === 0 && authorFirstRow) || (i === 1 && authorSecondRow);
					return (
						<div class={s.row} key={`item${index}row${i}`}>
							<HTML
								class={s.rowContent}
								markdown
								content={row.content}
								onClickCapture={maybeNavInternally}
							/>
							{authorData.content && authorInline && author}
						</div>
					);
				})}
				{authorData.content && authorStacked && author}
			</div>
		);
	}

	renderCrumbs() {
		const { activeIndex } = this.state;

		return (
			<div class={s.crumbContainer}>
				{this.items.map((item, i) => {
					const isActive = activeIndex === i;
					return <div class={joinClasses(s.dot, isActive && s.active)} key={`crumb-${i}`} />;
				})}
			</div>
		);
	}

	render({ data, position, isShowtimes }) {
		const { headerHeight, carouselHeight } = this.state;

		let containerStyle = '';
		if (!isShowtimes) {
			let topStyle = 'auto';
			if (!position || position === 'top') {
				topStyle = `${+headerHeight + 16}px`;
			} else if (position === 'left' || position === 'right') {
				topStyle = '50%';
			}
			containerStyle = `top: ${topStyle}; --carousel-height: ${carouselHeight}px;`;
		}

		const showCrumbs = data.showCrumbs && this.items?.length > 1;

		const carouselContainer = (
			<div class={joinClasses(s.container, s[position], isShowtimes && s.isShowtimes)} style={containerStyle}>
				{this.items?.length > 0 && this.items.map((item, index) => this.renderItem(item, index))}
			</div>
		);

		if (showCrumbs) {
			return (
				<div class={s.outerWrapper}>
					{carouselContainer}
					{this.renderCrumbs()}
				</div>
			);
		}

		return carouselContainer;
	}
}